/**
 * Cartão do convite: enquanto ninguém entrou, a tela do atendente mostra o contato com anéis que se
 * abrem, o título da chamada e o botão para copiar o link. Quando o primeiro convidado entra, o cartão
 * voa até o botão de compartilhar da barra e fica ali, fixo (ver `inviteLanding`).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, Link2, TriangleAlert, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { playInviteLanding } from './inviteLanding';
import { initialsOf, toneFor } from './participantDisplay';
import { useCopyFeedback } from './useCopyFeedback';

export const INVITE_WAITING_TEXT = 'Aguardando o primeiro convidado entrar';

/** Fases do cartão: na tela, voando até a barra, ou já guardado. */
export function useInvitePhase(shouldShow) {
    const [phase, setPhase] = useState(shouldShow ? 'shown' : 'gone');
    useEffect(() => {
        if (phase === 'shown' && !shouldShow) setPhase('leaving');
    }, [phase, shouldShow]);
    return [phase, () => setPhase('gone')];
}

// Tamanhos do avatar e dos três anéis (do menor para o maior) em cada variante.
const AVATAR = Object.freeze({
    compact: { box: 'h-20 w-20', avatar: 'h-11 w-11 text-sm', icon: 'h-5 w-5', rings: ['h-14 w-14', 'h-[68px] w-[68px]', 'h-20 w-20'] },
    room: { box: 'h-[136px] w-[136px]', avatar: 'h-16 w-16 text-xl', icon: 'h-7 w-7', rings: ['h-24 w-24', 'h-[114px] w-[114px]', 'h-[136px] w-[136px]'] },
});

/** Contato (iniciais) ou, sem nome, o ícone de vídeo, no centro de anéis concêntricos que se abrem. */
function InviteAvatar({ contactName, compact }) {
    const size = AVATAR[compact ? 'compact' : 'room'];
    return (
        // Na janela flutuante muito baixa o avatar some (a media query é do documento da própria janela): o título e o
        // botão para copiar o link são o que importa.
        <div aria-hidden="true" data-invite-avatar="" className={cn('relative mb-2 flex items-center justify-center', size.box, compact && '[@media(max-height:340px)]:hidden')}>
            {size.rings.map((ring, index) => (
                <span
                    key={ring}
                    style={{ animationDelay: `${index * 0.55}s` }}
                    className={cn(
                        'absolute rounded-full border border-cyan-400/50 animate-[invite-ring_2.6s_ease-out_infinite]',
                        'motion-reduce:animate-none motion-reduce:opacity-40',
                        ring,
                    )}
                />
            ))}
            {contactName ? (
                <span className={cn('relative flex items-center justify-center rounded-full font-bold text-white', size.avatar, toneFor(contactName))}>
                    {initialsOf(contactName)}
                </span>
            ) : (
                <span className={cn('relative flex items-center justify-center rounded-full bg-cyan-500/20 text-cyan-300', size.avatar)}>
                    <Video className={size.icon} />
                </span>
            )}
        </div>
    );
}

const displayUrl = (url) => url.replace(/^https?:\/\//, '');

const COPY_LABEL = Object.freeze({ idle: 'Copiar link', copied: 'Link copiado', failed: 'Não foi possível copiar' });

/**
 * Parte visual, sem animação de voo.
 * @param {{
 *   title: string, url: string, contactName?: string, variant: 'compact' | 'room' | 'guest',
 *   onCopy: (event: React.MouseEvent) => void, copyState?: 'idle' | 'copied' | 'failed',
 * }} props
 */
export function InviteCardView({ title, url, contactName, variant, onCopy, copyState = 'idle', cardRef, contentRef }) {
    const compact = variant === 'compact';
    return (
        <div className={cn('pointer-events-none absolute inset-0 z-[5] flex items-center justify-center p-4', compact && 'pb-[76px]')}>
            <section
                ref={cardRef}
                aria-label="Convite da chamada"
                className={cn(
                    'pointer-events-auto w-full rounded-3xl border border-white/10 bg-neutral-900/85 text-center shadow-2xl backdrop-blur-md',
                    compact ? 'max-w-[300px] px-4 pb-3 pt-3' : 'max-w-[400px] px-7 pb-5 pt-4',
                )}
            >
                <div ref={contentRef} className="flex flex-col items-center">
                    <InviteAvatar contactName={contactName} compact={compact} />
                    <h2 className={cn('line-clamp-2 font-semibold leading-snug text-white', compact ? 'text-sm' : 'text-lg')}>{title}</h2>
                    <p className={cn('mt-1 text-neutral-400', compact ? 'text-xs' : 'text-sm')}>{INVITE_WAITING_TEXT}</p>
                    <button
                        type="button"
                        onClick={onCopy}
                        className={cn(
                            'inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 font-medium text-neutral-50 transition-colors hover:bg-white/10',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
                            compact ? 'mt-3 h-9 text-sm' : 'mt-4 h-10 text-sm',
                            copyState === 'copied' && 'border-cyan-400/60 text-cyan-300',
                            copyState === 'failed' && 'border-amber-400/60 text-amber-300',
                        )}
                    >
                        {copyState === 'copied' && <Check className="h-4 w-4" aria-hidden="true" />}
                        {copyState === 'failed' && <TriangleAlert className="h-4 w-4" aria-hidden="true" />}
                        {copyState === 'idle' && <Link2 className="h-4 w-4" aria-hidden="true" />}
                        {COPY_LABEL[copyState]}
                    </button>
                    {!compact && (
                        <code title={url} className="mt-2 block max-w-full select-all truncate text-[11px] text-neutral-500">{displayUrl(url)}</code>
                    )}
                </div>
            </section>
        </div>
    );
}

/**
 * Cartão com a animação: some do DOM depois de pousar no botão de destino.
 * @param {{
 *   invite: { title: string, url: string, contactName?: string }, variant: 'compact' | 'room' | 'guest',
 *   phase: 'shown' | 'leaving', onCopy: (doc: Document) => unknown, onGone: () => void,
 *   targetRef: React.RefObject<HTMLElement>,
 * }} props
 */
export function InviteCard({ invite, variant, phase, onCopy, onGone, targetRef }) {
    const cardRef = useRef(null);
    const contentRef = useRef(null);
    const [copyState, copy] = useCopyFeedback(onCopy);

    useLayoutEffect(() => {
        if (phase !== 'leaving') return undefined;
        return playInviteLanding({ card: cardRef.current, content: contentRef.current, target: targetRef.current, onDone: onGone });
        // `onGone` troca a cada render; a animação só deve começar uma vez, quando a fase vira "leaving".
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    return (
        <InviteCardView
            title={invite.title}
            url={invite.url}
            contactName={invite.contactName}
            variant={variant}
            onCopy={(event) => copy(event.currentTarget.ownerDocument)}
            copyState={copyState}
            cardRef={cardRef}
            contentRef={contentRef}
        />
    );
}
